import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  setSelectedAccountId,
  setSelectedLocationId,
  setRatingFilter,
  setIncludeRatingOnly,
  fetchLocations,
  fetchReviews,
} from '../../store/slices/googleReviewSlice';

const RATINGS = [5, 4, 3, 2, 1];
const MAX_LABEL = 20;
const truncate = (s: string) => s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL)}…` : s;

const ReviewFilters: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    accounts,
    locations,
    selectedAccountId,
    selectedLocationId,
    ratingFilter,
    includeRatingOnly,
    activeTab,
  } = useAppSelector((s) => s.googleReview);

  // Status filtering, rating, and the rating-only toggle are all server-side now, so every
  // filter change refetches carrying the full active filter set.
  const filter = activeTab === 'all' ? undefined : activeTab;

  const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value || null;
    dispatch(setSelectedAccountId(accountId));
    if (accountId) {
      dispatch(fetchLocations(accountId));
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const locationId = e.target.value || null;
    dispatch(setSelectedLocationId(locationId));
    if (locationId) {
      dispatch(fetchReviews({ locationId, filter, rating: ratingFilter, includeRatingOnly }));
    }
  };

  const handleRatingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const rating = e.target.value ? Number(e.target.value) : null;
    dispatch(setRatingFilter(rating));
    if (selectedLocationId) {
      dispatch(fetchReviews({ locationId: selectedLocationId, filter, rating, includeRatingOnly }));
    }
  };

  const handleIncludeRatingOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    dispatch(setIncludeRatingOnly(value));
    if (selectedLocationId) {
      dispatch(fetchReviews({ locationId: selectedLocationId, filter, rating: ratingFilter, includeRatingOnly: value }));
    }
  };

  const selectedAccount = accounts.find((a) => a.accountId === selectedAccountId);
  const selectedLocation = locations.find((l) => l.locationId === selectedLocationId);

  return (
    <div className="review-filters">
      <div className="review-filters__item">
        <label className="review-filters__label">{t('google_review_filter_account')}:</label>
        <select
          className="review-filters__select"
          value={selectedAccountId ?? ''}
          onChange={handleAccountChange}
          aria-label={t('google_review_filter_account')}
        >
          {!selectedAccount && <option value="">{t('google_review_filter_select_account')}</option>}
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId} title={a.accountName}>
              {truncate(a.accountName ?? '')}
            </option>
          ))}
        </select>
      </div>

      <div className="review-filters__item">
        <label className="review-filters__label">{t('google_review_filter_location')}:</label>
        <select
          className="review-filters__select"
          value={selectedLocationId ?? ''}
          onChange={handleLocationChange}
          disabled={!selectedAccountId}
          aria-label={t('google_review_filter_location')}
        >
          {!selectedLocation && <option value="">{t('google_review_filter_select_location')}</option>}
          {locations.map((l) => (
            <option key={l.locationId} value={l.locationId} title={l.locationName}>
              {truncate(l.locationName ?? '')}
            </option>
          ))}
        </select>
      </div>

      <select
        className="review-filters__select review-filters__select--standalone"
        value={ratingFilter ?? ''}
        onChange={handleRatingChange}
        aria-label={t('google_review_filter_rating')}
      >
        <option value="">{t('google_review_filter_rating_all')}</option>
        {RATINGS.map((r) => (
          <option key={r} value={r}>
            {'★'.repeat(r)} ({r})
          </option>
        ))}
      </select>

      <label className="review-filters__checkbox">
        <input
          type="checkbox"
          checked={includeRatingOnly}
          onChange={handleIncludeRatingOnlyChange}
        />
        {t('google_review_filter_include_rating_only')}
      </label>
    </div>
  );
};

export default ReviewFilters;
